import { timeAgo } from '@bpinternal/shared'
import clsx from 'clsx'
import { ChevronRight, SquarePen, User } from 'lucide-react'
import { ComponentProps } from 'react'
import { webchatClasses } from '../../styles/classes'
import { Avatar } from '../Avatar'
import { Loader } from '../Loader'
import { Conversations } from '../../hooks'
import { CloseWindowButton } from '../CloseWindowButton'

const capitalizeFirstLetter = (input: string) => {
  if (input === null || input === undefined || input.length === 0) {
    return ''
  }
  return input.charAt(0).toUpperCase() + input.slice(1)
}

type ConversationHistoryProps = {
  onConversationClick?: (conversationId: string) => void
  onNewConversation?: () => void
  onClose?: () => void
  currentConversationId?: string
  isLoading?: boolean
  conversations: Conversations
  botAvatar?: string
  botName?: string
}

export const ConversationHistory = ({
  className,
  onConversationClick,
  onNewConversation,
  onClose,
  currentConversationId,
  isLoading,
  conversations,
  botAvatar,
  botName,
  ...props
}: ComponentProps<'div'> & ConversationHistoryProps) => {
  const { conversationHistory } = webchatClasses

  return (
    <div
      {...props}
      {...conversationHistory.container}
      className={clsx(conversationHistory.container.className, className)}
    >
      <div {...conversationHistory.header}>
        Conversations
        {onClose && (
          <div {...conversationHistory.closeButton}>
            <CloseWindowButton onClick={onClose} />
          </div>
        )}
      </div>

      {isLoading ? (
        <div {...conversationHistory.loaderContainer}>
          <Loader />
        </div>
      ) : (
        <div {...conversationHistory.scrollContainer}>
          {conversations.length === 0 ? (
            <div {...conversationHistory.loaderContainer}>No conversation started yet</div>
          ) : (
            conversations.map(({ id, lastMessage }) => {
              let content = ''
              if (lastMessage.payload.type === 'text') {
                content = lastMessage.payload.text
              } else if (
                lastMessage.payload.type === 'bloc' &&
                lastMessage.payload.items.some((item) => item.type === 'text')
              ) {
                const textBloc = lastMessage.payload.items.find((item) => item.type === 'text')
                content = textBloc?.payload.text ?? ''
              } else {
                content = 'Sent an attachment'
              }
              return (
                <button
                  key={id}
                  {...conversationHistory.conversation.container}
                  onClick={() => onConversationClick?.(id)}
                  data-active={currentConversationId === id ? '' : undefined}
                >
                  {lastMessage.author.type === 'bot' ? (
                    <Avatar userId={lastMessage.userId} src={botAvatar} {...conversationHistory.conversation.avatar}>
                      {botName ?? 'Bot'}
                    </Avatar>
                  ) : (
                    <Avatar
                      userId={lastMessage.userId}
                      {...conversationHistory.conversation.avatar}
                      fallback={conversationHistory.conversation.userAvatar.className}
                    >
                      <User strokeWidth={1.5} />
                    </Avatar>
                  )}
                  <div {...conversationHistory.conversation.content}>
                    <span {...conversationHistory.conversation.name}>{content}</span>
                    <span {...conversationHistory.conversation.timestamp}>
                      {capitalizeFirstLetter(timeAgo(lastMessage.createdAt))}
                    </span>
                  </div>
                  <ChevronRight strokeWidth={1.5} {...conversationHistory.conversation.chevron} />
                </button>
              )
            })
          )}
        </div>
      )}

      <button {...conversationHistory.newConversation.button} onClick={onNewConversation}>
        <SquarePen {...conversationHistory.newConversation.icon} />
        New conversation
      </button>
    </div>
  )
}

ConversationHistory.displayName = 'ConversationHistory'
